import React from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Stack,
  IconButton,
  Button,
  Chip,
  Paper,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableRow,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import {
  CalendarToday as CalendarIcon,
  TrendingUp as TrendingIcon,
  FitnessCenter as ExerciseIcon,
  Restaurant as NutritionIcon,
  PhotoCamera as PhotoIcon,
  Scale as WeightIcon,
  MoreVert as MoreIcon,
  Add as AddIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  DirectionsRun as RunningIcon,
  LocalDrink as WaterIcon,
  Grass as FiberIcon,
  MonitorHeart as MonitorHeartIcon,
  Sports as ActivitiesIcon,
  QuestionAnswer as QuestionIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { useRecoilValue } from "recoil";
import organizationIdState from "../../../../../atoms/organizationIdAtom";
import useDailyReports from "../../../../../hooks/useDailyReports";
import { useOrganizationDataByOrgId } from "../../../../../hooks/useOrganizationData";
import DailyOptionalQuestionsSection from "../../../../../components/organisms/DailyOptionalQuestionsSection";
import useWaterUnit from "../../../../../hooks/useWaterUnit";
import PhotoThumbnails from "../../../../../components/PhotoThumbnails";
import ExerciseDetails from "../../../../../components/ExerciseDetails";
import NutritionDetails from "../../../../../components/NutritionDetails";
import BodyMeasurementsEditDialog from "../BodyMeasurementsEditDialog";
import MonthYearPicker from "../../../../../components/molecules/MonthYearPicker";
import { getMonthRange } from "../../../../../utils/monthDateRange";

// Move this outside the component so it's stable across renders
const TODAY = new Date();
const TODAY_MONTH_KEY = getMonthRange(TODAY).key;

// Shared styled components (consistent with other tabs)
const StyledDailyCard = styled(Card)(({ theme }) => ({
  height: "auto",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(2),
  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  transition: "box-shadow 0.3s ease",
  "&:hover": {
    boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
  },
}));

const DailyHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
}));

const DailyContent = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(3),
  paddingTop: theme.spacing(2),
}));

const CalendarDay = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isSelected" && prop !== "isToday",
})(({ theme, isSelected, isToday }) => ({
  width: "100%",
  height: 46,
  borderRadius: 8,
  cursor: "pointer",
  position: "relative",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: isSelected ? theme.palette.primary.main : "transparent",
  color: isSelected
    ? "white"
    : isToday
      ? theme.palette.primary.main
      : theme.palette.text.primary,
  border:
    isToday && !isSelected ? `2px solid ${theme.palette.primary.main}` : "none",
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: isSelected
      ? theme.palette.primary.main
      : theme.palette.action.hover,
  },
  "& .day-number": {
    position: "absolute",
    top: 5,
    left: "50%",
    transform: "translateX(-50%)",
    margin: 0,
    padding: 0,
    textAlign: "center",
    width: "100%",
    boxSizing: "border-box",
    lineHeight: 1.2,
  },
  "& .data-indicators": {
    position: "absolute",
    bottom: 3,
    left: 0,
    right: 0,
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center",
    alignContent: "flex-end",
    gap: "2px",
    padding: "0 3px",
    maxHeight: 14,
    overflow: "hidden",
  },
  "& .data-dot": {
    width: 5,
    height: 5,
    borderRadius: "50%",
    flexShrink: 0,
  },
  "& .nutrition-dot": {
    backgroundColor: isSelected ? "white" : theme.palette.success.main,
  },
  "& .exercise-dot": {
    backgroundColor: isSelected ? "white" : theme.palette.info.main,
  },
  "& .exercise-dot-incomplete": {
    backgroundColor: isSelected ? "white" : theme.palette.warning.main,
  },
  "& .photo-dot": {
    backgroundColor: isSelected ? "white" : theme.palette.error.main,
  },
  "& .activity-dot": {
    backgroundColor: isSelected ? "white" : theme.palette.secondary.main,
  },
  "& .questions-dot": {
    backgroundColor: isSelected ? "white" : "#7C3AED",
  },
}));

const ActionButton = styled(Button)(() => ({
  borderRadius: 20,
  padding: "8px 20px",
  textTransform: "none",
  fontWeight: 600,
}));

const CALENDAR_LEGEND_ITEMS = [
  { swatch: "success.main", label: "Nutrition" },
  { swatch: "info.main", label: "Ex. complete" },
  { swatch: "warning.main", label: "Ex. incomplete" },
  { swatch: "secondary.main", label: "Activities" },
  { swatch: "error.main", label: "Photos" },
  { swatch: "#7C3AED", label: "Check-in" },
];

// Enhanced Daily Reports Tab Component
const EnhancedDailyReportsTab = ({ client }) => {
  const { waterLabel } = useWaterUnit();
  const organizationId = useRecoilValue(organizationIdState);
  const { organizationData, loading: orgLoading } = useOrganizationDataByOrgId({
    organizationId,
  });
  // Use the custom hook to fetch and manage daily reports (month-paged)
  const {
    loading,
    monthLoading,
    calendarModifiers,
    selectedDay,
    dailyReport,
    recentReportSummaries,
    handleDayClick,
    refreshSelectedDayReport,
    ensureMonthLoaded,
  } = useDailyReports({
    clientId: client?.uid,
  });

  // Local state for dialog
  const [bodyMeasurementOpen, setBodyMeasurementOpen] = React.useState(false);

  // Handler to open dialog
  const openBodyMeasurementDialog = () => setBodyMeasurementOpen(true);
  // Handler to close dialog and refresh data
  const handleBodyMeasurementDialogClosed = async () => {
    setBodyMeasurementOpen(false);
    await refreshSelectedDayReport();
  };

  // Calendar month view — drives paging
  const [monthKey, setMonthKey] = React.useState(TODAY_MONTH_KEY);
  const year = Number(monthKey.slice(0, 4));
  const month = Number(monthKey.slice(5, 7)) - 1;

  React.useEffect(() => {
    if (!client?.uid) return undefined;
    let mounted = true;
    const load = async () => {
      await ensureMonthLoaded(monthKey);
      if (!mounted) return;
    };
    load();
    return () => {
      mounted = false;
    };
  }, [client?.uid, monthKey, ensureMonthLoaded]);

  const handleMonthKeyChange = React.useCallback((key) => {
    setMonthKey(key);
  }, []);

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDayOfWeek = new Date(year, month, 1).getDay();
  // const today = new Date();

  const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  // Generate calendar grid
  const calendarDays = [];
  // Add empty cells for days before first day of month
  for (let i = 0; i < firstDayOfWeek; i++) {
    calendarDays.push(null);
  }
  // Add days of the month
  for (let day = 1; day <= daysInMonth; day++) {
    calendarDays.push(day);
  }

  const isToday = (day) => {
    if (!day) return false;
    const date = new Date(year, month, day);
    return date.toDateString() === TODAY.toDateString();
  };

  const isSelected = (day) => {
    if (!day || !selectedDay) return false;
    const date = new Date(year, month, day);
    return date.toDateString() === selectedDay.toDateString();
  };

  const getDataForDay = React.useMemo(
    () => (day) => {
      if (!day) return null;
      const date = new Date(year, month, day);
      const dateString = date.toDateString();
      return calendarModifiers?.[dateString] || null;
    },
    [calendarModifiers, year, month],
  );

  // Display nutrition data in a compact format
  const formatMacros = (nutrition) => {
    if (!nutrition) return null;

    const totalMacros = nutrition.proteins + nutrition.carbs + nutrition.fats;
    if (totalMacros === 0) return null;

    const fiber =
      typeof nutrition?.micronutrients?.fiber === "number"
        ? nutrition.micronutrients.fiber
        : 0;
    const fiberText = fiber > 0 ? ` · Fi: ${Math.round(fiber * 10) / 10}g` : "";
    return `P: ${Math.round(nutrition.proteins)}g · C: ${Math.round(nutrition.carbs)}g · F: ${Math.round(nutrition.fats)}g${fiberText}`;
  };

  // Create exercise completion text
  const formatExerciseCompletion = (exercise) => {
    if (!exercise) return null;

    if (exercise.totalExercises === 0) return null;

    return `${exercise.completedExercises}/${exercise.totalExercises} exercises`;
  };

  // Ensure today is selected and highlighted on first load
  const didSelectToday = React.useRef(false);

  React.useEffect(() => {
    if (!didSelectToday.current) {
      handleDayClick(TODAY);
      didSelectToday.current = true;
    }
    // eslint-disable-next-line
  }, []);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Modified grid layout to include Recent Reports below calendar
  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={3}>
        {/* Left Column - Calendar and Recent Reports */}
        <Grid item xs={12} md={8}>
          {/* Calendar View */}
          <StyledDailyCard>
            <DailyHeader>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <CalendarIcon color="primary" sx={{ fontSize: 28 }} />
                <Typography variant="h6" fontWeight="bold">
                  Daily reports
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <MonthYearPicker
                  value={monthKey}
                  onChange={handleMonthKeyChange}
                  showToday
                />
                <ActionButton
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setMonthKey(TODAY_MONTH_KEY);
                    handleDayClick(TODAY);
                  }}
                >
                  Today
                </ActionButton>
              </Stack>
            </DailyHeader>
            <DailyContent>
              {monthLoading && (
                <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              )}
              {/* Calendar Header */}
              <Grid container spacing={1} sx={{ mb: 2 }}>
                {weekDays.map((day) => (
                  <Grid item xs={12 / 7} key={day}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      align="center"
                      fontWeight="bold"
                      sx={{ py: 1 }}
                    >
                      {day}
                    </Typography>
                  </Grid>
                ))}
              </Grid>

              {/* Calendar Days */}
              <Grid container spacing={1}>
                {calendarDays.map((day, index) => {
                  if (!day) {
                    return (
                      <Grid item xs={12 / 7} key={`empty-${index}`}></Grid>
                    );
                  }

                  return (
                    <Grid item xs={12 / 7} key={day}>
                      <CalendarDay
                        isSelected={isSelected(day)}
                        isToday={isToday(day)}
                        onClick={() => {
                          const date = new Date(year, month, day);
                          handleDayClick(date);
                        }}
                      >
                        <Typography
                          variant="body2"
                          fontWeight={isToday(day) ? "bold" : "normal"}
                          className="day-number"
                        >
                          {day}
                        </Typography>
                        <div className="data-indicators">
                          {(() => {
                            const data = getDataForDay(day);
                            if (!data) return null;

                            const getExerciseDotClass = () => {
                              if (!data.hasExercise) return null;
                              return data.exerciseStatus === "complete"
                                ? "data-dot exercise-dot"
                                : "data-dot exercise-dot-incomplete";
                            };

                            return (
                              <>
                                {data.hasNutrition && (
                                  <div className="data-dot nutrition-dot" />
                                )}
                                {data.hasExercise && (
                                  <div className={getExerciseDotClass()} />
                                )}
                                {data.hasActivities && (
                                  <div className="data-dot activity-dot" />
                                )}
                                {data.hasPhotos && (
                                  <div className="data-dot photo-dot" />
                                )}
                                {data.hasQuestions && (
                                  <div className="data-dot questions-dot" />
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </CalendarDay>
                    </Grid>
                  );
                })}
              </Grid>

              {/* Calendar Legend — wraps on narrow widths */}
              <Box
                sx={{
                  mt: 2.5,
                  pt: 2,
                  borderTop: 1,
                  borderColor: "divider",
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "center",
                  alignItems: "center",
                  columnGap: { xs: 1.25, sm: 2 },
                  rowGap: 1,
                }}
              >
                {CALENDAR_LEGEND_ITEMS.map(({ swatch, label }) => (
                  <Stack
                    key={label}
                    direction="row"
                    alignItems="center"
                    spacing={0.75}
                    sx={{ flex: "0 0 auto", px: 0.25 }}
                  >
                    <Box
                      sx={{
                        width: 7,
                        height: 7,
                        borderRadius: "50%",
                        bgcolor: swatch,
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="caption" sx={{ whiteSpace: "nowrap" }}>
                      {label}
                    </Typography>
                  </Stack>
                ))}
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={0.75}
                  sx={{ flex: "0 0 auto", px: 0.25 }}
                >
                  <Box
                    sx={{
                      width: 9,
                      height: 9,
                      border: 2,
                      borderColor: "primary.main",
                      borderRadius: "50%",
                      flexShrink: 0,
                      boxSizing: "border-box",
                    }}
                  />
                  <Typography variant="caption" sx={{ whiteSpace: "nowrap" }}>
                    Today
                  </Typography>
                </Stack>
              </Box>
            </DailyContent>
          </StyledDailyCard>

          {/* Recent Reports - Now with detailed summary data */}
          <StyledDailyCard sx={{ mt: 3 }}>
            <DailyHeader>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <CalendarIcon color="primary" sx={{ fontSize: 28 }} />
                <Typography variant="h6" fontWeight="bold">
                  Recent Reports
                </Typography>
              </Stack>
              <ActionButton
                variant="outlined"
                size="small"
                disabled={recentReportSummaries.length === 0}
              >
                View All
              </ActionButton>
            </DailyHeader>
            <DailyContent>
              {recentReportSummaries.length > 0 ? (
                <Grid container spacing={2}>
                  {recentReportSummaries.slice(0, 6).map((report, index) => (
                    <Grid item xs={12} md={6} key={index}>
                      <Paper
                        elevation={1}
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          cursor: "pointer",
                          transition: "all 0.2s",
                          "&:hover": {
                            boxShadow: 3,
                          },
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          minHeight: "165px", // Add minimum height to ensure consistent card sizing
                        }}
                        onClick={() => handleDayClick(report.date)}
                      >
                        {/* Report Header with Date */}
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 1,
                          }}
                        >
                          <Typography variant="subtitle1" fontWeight="bold">
                            {new Date(report.date).toLocaleDateString("en-US", {
                              weekday: "short",
                              month: "short",
                              day: "numeric",
                            })}
                          </Typography>
                          {report.weight && (
                            <Chip
                              icon={<WeightIcon />}
                              label={`${Math.round(report.weight * 10) / 10} lbs`}
                              color="primary"
                              size="small"
                            />
                          )}
                        </Box>

                        <Divider sx={{ mb: 1.5 }} />

                        {/* Exercise Summary Section */}
                        {report.exercise ? (
                          <Box sx={{ mb: 1.5 }}>
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              sx={{ mb: 0.5 }}
                            >
                              <ExerciseIcon
                                fontSize="small"
                                color={
                                  report.exercise.completedExercises > 0
                                    ? "success"
                                    : "action"
                                }
                              />
                              <Typography variant="body2" fontWeight="medium">
                                {report.exercise.splitName || "Workout"}
                              </Typography>
                            </Stack>

                            {formatExerciseCompletion(report.exercise) && (
                              <Box sx={{ ml: 3 }}>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  component="span"
                                >
                                  {formatExerciseCompletion(report.exercise)}
                                </Typography>
                                <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, display: "inline-flex" }}>
                                  {report.exercise.status === "complete" && (
                                    <Chip
                                      label="Completed"
                                      size="small"
                                      color="success"
                                      variant="outlined"
                                      sx={{ height: 20 }}
                                    />
                                  )}
                                  {/* Show skipped count if available in exercise data */}
                                  {(report.exercise.skippedExercises > 0 || report.exercise.hasSkipped) && (
                                    <Chip
                                      label={report.exercise.skippedExercises > 0 ? `${report.exercise.skippedExercises} Skipped` : "Skipped"}
                                      size="small"
                                      color="warning"
                                      variant="outlined"
                                      sx={{ height: 20 }}
                                    />
                                  )}
                                </Stack>
                              </Box>
                            )}

                            {report.exercise.protocolName && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ ml: 3, display: "block" }}
                              >
                                {report.exercise.protocolName}
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Box sx={{ mb: 1.5 }}>
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              sx={{ mb: 0.5 }}
                            >
                              <ExerciseIcon fontSize="small" color="disabled" />
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                No workout data
                              </Typography>
                            </Stack>
                          </Box>
                        )}

                        {/* Nutrition Summary Section */}
                        {report.nutrition && formatMacros(report.nutrition) ? (
                          <Box
                            sx={{
                              mb:
                                report.nutrition.steps || report.nutrition.water
                                  ? 1.5
                                  : 0,
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              sx={{ mb: 0.5 }}
                            >
                              <NutritionIcon fontSize="small" color="primary" />
                              <Typography variant="body2" fontWeight="medium">
                                Nutrition
                              </Typography>
                            </Stack>

                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ ml: 3 }}
                            >
                              {formatMacros(report.nutrition)}
                            </Typography>
                          </Box>
                        ) : (
                          <Box sx={{ mb: 1.5 }}>
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              sx={{ mb: 0.5 }}
                            >
                              <NutritionIcon
                                fontSize="small"
                                color="disabled"
                              />
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                No nutrition data
                              </Typography>
                            </Stack>
                          </Box>
                        )}

                        {report.hasQuestions && (
                          <Box sx={{ mb: 1.5 }}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <QuestionIcon fontSize="small" sx={{ color: "#7C3AED" }} />
                              <Typography variant="body2" fontWeight="medium">
                                Daily check-in answered
                              </Typography>
                            </Stack>
                          </Box>
                        )}

                        {/* Activities Summary */}
                        {report.hasActivities && (
                          <Box sx={{ mb: 1.5 }}>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                              <ActivitiesIcon fontSize="small" color="secondary" />
                              <Typography variant="body2" fontWeight="medium">
                                {report.activities.length === 1
                                  ? report.activities[0].activityName
                                  : `${report.activities.length} activities`}
                              </Typography>
                            </Stack>
                            {report.activities.length === 1 && report.activities[0].durationMinutes > 0 && (
                              <Typography variant="body2" color="text.secondary" sx={{ ml: 3 }}>
                                {report.activities[0].durationMinutes < 60
                                  ? `${report.activities[0].durationMinutes}m`
                                  : `${Math.floor(report.activities[0].durationMinutes / 60)}h ${report.activities[0].durationMinutes % 60}m`}
                                {report.activities[0].distanceMeters > 0 && ` · ${report.activities[0].distanceMeters >= 1000 ? `${(report.activities[0].distanceMeters / 1000).toFixed(2)} km` : `${report.activities[0].distanceMeters} m`}`}
                              </Typography>
                            )}
                          </Box>
                        )}

                        {/* Additional Metrics */}
                        {(report.nutrition?.steps > 0 ||
                          report.nutrition?.water > 0 ||
                          (report.nutrition?.micronutrients?.fiber || 0) > 0) && (
                          <Stack
                            direction="row"
                            spacing={2}
                            sx={{ mt: "auto", pt: 1 }}
                          >
                            {report.nutrition.steps > 0 && (
                              <Tooltip title="Steps">
                                <Chip
                                  icon={<RunningIcon />}
                                  label={`${Math.round(report.nutrition.steps)}`}
                                  size="small"
                                  variant="outlined"
                                />
                              </Tooltip>
                            )}

                            {report.nutrition.water > 0 && (
                              <Tooltip title="Water Intake">
                                <Chip
                                  icon={<WaterIcon />}
                                  label={waterLabel(report.nutrition.water)}
                                  size="small"
                                  variant="outlined"
                                  color="info"
                                />
                              </Tooltip>
                            )}
                            {(report.nutrition?.micronutrients?.fiber || 0) > 0 && (
                              <Tooltip title="Fiber Intake">
                                <Chip
                                  icon={<FiberIcon />}
                                  label={`${Math.round((report.nutrition.micronutrients.fiber || 0) * 10) / 10} g`}
                                  size="small"
                                  variant="outlined"
                                  color="success"
                                />
                              </Tooltip>
                            )}
                          </Stack>
                        )}
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Paper
                  sx={{
                    p: 3,
                    textAlign: "center",
                    bgcolor: "grey.50",
                    borderStyle: "dashed",
                    borderWidth: 2,
                    borderColor: "grey.300",
                  }}
                >
                  <CalendarIcon
                    sx={{ fontSize: 48, color: "grey.400", mb: 2 }}
                  />
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    No recent reports
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 3 }}
                  >
                    Start tracking to see your progress
                  </Typography>
                  <ActionButton
                    variant="outlined"
                    onClick={openBodyMeasurementDialog}
                    startIcon={<AddIcon />}
                  >
                    Add Your First Report
                  </ActionButton>
                </Paper>
              )}
            </DailyContent>
          </StyledDailyCard>
        </Grid>

        {/* Right Column - Daily Summary */}
        <Grid item xs={12} md={4}>
          <StyledDailyCard>
            <DailyHeader>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <TrendingIcon color="primary" sx={{ fontSize: 28 }} />
                <Typography variant="h6" fontWeight="bold">
                  Daily Summary
                </Typography>
              </Stack>
              <IconButton size="small" onClick={openBodyMeasurementDialog}>
                <MoreIcon />
              </IconButton>
            </DailyHeader>
            <DailyContent>
              {selectedDay ? (
                <Box>
                  <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                    {selectedDay.toLocaleDateString("en-US", {
                      weekday: "long",
                      month: "long",
                      day: "numeric",
                    })}
                  </Typography>

                  {dailyReport ? (
                    <Stack spacing={3}>
                      {/* Weight */}
                      {dailyReport.weight && (
                        <Box>
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                            sx={{ mb: 1 }}
                          >
                            <WeightIcon color="primary" />
                            <Typography variant="body2" color="text.secondary">
                              Weight
                            </Typography>
                          </Stack>
                          <Typography variant="h4" fontWeight="bold">
                            {Math.round(dailyReport.weight * 10) / 10} lbs
                          </Typography>
                        </Box>
                      )}

                      <Divider />

                      {/* Activity Summary */}
                      <Stack spacing={2}>
                        <Table size="small">
                          <TableBody>
                            <TableRow>
                              <TableCell sx={{ borderBottom: "none", pl: 0 }}>
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  spacing={1}
                                >
                                  <ExerciseIcon
                                    color={
                                      dailyReport.hasExercise
                                        ? "success"
                                        : "disabled"
                                    }
                                    fontSize="small"
                                  />
                                  <Typography variant="body2">
                                    Exercise
                                  </Typography>
                                </Stack>
                              </TableCell>
                              <TableCell
                                sx={{ borderBottom: "none", pr: 0 }}
                                align="right"
                              >
                                <Chip
                                  label={
                                    dailyReport.hasExercise
                                      ? "Completed"
                                      : "Not Done"
                                  }
                                  color={
                                    dailyReport.hasExercise
                                      ? "success"
                                      : "default"
                                  }
                                  size="small"
                                  icon={
                                    dailyReport.hasExercise ? (
                                      <CheckIcon />
                                    ) : (
                                      <WarningIcon />
                                    )
                                  }
                                />
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ borderBottom: "none", pl: 0 }}>
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  spacing={1}
                                >
                                  <MonitorHeartIcon
                                    color={
                                      dailyReport.hasNutrition
                                        ? "success"
                                        : "disabled"
                                    }
                                    fontSize="small"
                                  />
                                  <Typography variant="body2">
                                    Health Metrics
                                  </Typography>
                                </Stack>
                              </TableCell>
                              <TableCell
                                sx={{ borderBottom: "none", pr: 0 }}
                                align="right"
                              >
                                <Chip
                                  label={
                                    dailyReport.hasNutrition
                                      ? "Logged"
                                      : "Not Logged"
                                  }
                                  color={
                                    dailyReport.hasNutrition
                                      ? "success"
                                      : "default"
                                  }
                                  size="small"
                                  icon={
                                    dailyReport.hasNutrition ? (
                                      <CheckIcon />
                                    ) : (
                                      <WarningIcon />
                                    )
                                  }
                                />
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ borderBottom: "none", pl: 0 }}>
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  spacing={1}
                                >
                                  <ActivitiesIcon
                                    color={
                                      dailyReport.hasActivities
                                        ? "secondary"
                                        : "disabled"
                                    }
                                    fontSize="small"
                                  />
                                  <Typography variant="body2">
                                    Health Activities
                                  </Typography>
                                </Stack>
                              </TableCell>
                              <TableCell
                                sx={{ borderBottom: "none", pr: 0 }}
                                align="right"
                              >
                                <Chip
                                  label={
                                    dailyReport.hasActivities
                                      ? `${dailyReport.activities.length} logged`
                                      : "None"
                                  }
                                  color={
                                    dailyReport.hasActivities
                                      ? "secondary"
                                      : "default"
                                  }
                                  size="small"
                                  icon={
                                    dailyReport.hasActivities ? (
                                      <CheckIcon />
                                    ) : undefined
                                  }
                                />
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ borderBottom: "none", pl: 0 }}>
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  spacing={1}
                                >
                                  <PhotoIcon
                                    color={
                                      dailyReport.hasPhoto
                                        ? "success"
                                        : "disabled"
                                    }
                                    fontSize="small"
                                  />
                                  <Typography variant="body2">
                                    Progress Photo
                                  </Typography>
                                </Stack>
                              </TableCell>
                              <TableCell
                                sx={{ borderBottom: "none", pr: 0 }}
                                align="right"
                              >
                                <Chip
                                  label={
                                    dailyReport.hasPhoto
                                      ? "Uploaded"
                                      : "Not Uploaded"
                                  }
                                  color={
                                    dailyReport.hasPhoto ? "success" : "default"
                                  }
                                  size="small"
                                  icon={
                                    dailyReport.hasPhoto ? (
                                      <CheckIcon />
                                    ) : (
                                      <WarningIcon />
                                    )
                                  }
                                />
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ borderBottom: "none", pl: 0 }}>
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  spacing={1}
                                >
                                  <QuestionIcon
                                    sx={{
                                      color: dailyReport.hasQuestions
                                        ? "#7C3AED"
                                        : "action.disabled",
                                      fontSize: 20,
                                    }}
                                  />
                                  <Typography variant="body2">
                                    Check-in questions
                                  </Typography>
                                </Stack>
                              </TableCell>
                              <TableCell
                                sx={{ borderBottom: "none", pr: 0 }}
                                align="right"
                              >
                                <Chip
                                  label={
                                    dailyReport.hasQuestions
                                      ? "Answered"
                                      : "Not answered"
                                  }
                                  color={
                                    dailyReport.hasQuestions ? "secondary" : "default"
                                  }
                                  size="small"
                                  icon={
                                    dailyReport.hasQuestions ? (
                                      <CheckIcon />
                                    ) : (
                                      <WarningIcon />
                                    )
                                  }
                                  sx={
                                    dailyReport.hasQuestions
                                      ? {
                                          bgcolor: "#7C3AED14",
                                          color: "#7C3AED",
                                          borderColor: "#7C3AED55",
                                        }
                                      : undefined
                                  }
                                  variant={dailyReport.hasQuestions ? "outlined" : "filled"}
                                />
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </Stack>

                      {dailyReport.hasQuestions && (
                        <DailyOptionalQuestionsSection
                          optionalResponses={dailyReport.health?.reportData?.optional}
                          organizationOptionals={organizationData?.optional}
                          loading={orgLoading}
                        />
                      )}

                      {/* Render additional details */}
                      <NutritionDetails healthData={dailyReport.health} />
                      <ExerciseDetails exerciseData={dailyReport.exercise} />

                      {/* Health Activities from Apple Health */}
                      {dailyReport.hasActivities && (
                        <Box>
                          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                            <ActivitiesIcon color="secondary" fontSize="small" />
                            <Typography variant="subtitle2" fontWeight="bold">
                              Health Activities
                            </Typography>
                          </Stack>
                          <Stack spacing={1}>
                            {dailyReport.activities.map((activity, i) => (
                              <Box
                                key={i}
                                sx={{
                                  p: 1.5,
                                  borderRadius: 1,
                                  border: "1px solid",
                                  borderColor: "divider",
                                  bgcolor: "background.paper",
                                }}
                              >
                                <Typography variant="body2" fontWeight="medium">
                                  {activity.activityName}
                                </Typography>
                                <Stack direction="row" spacing={1} sx={{ mt: 0.5 }} flexWrap="wrap">
                                  {activity.durationMinutes > 0 && (
                                    <Chip
                                      label={activity.durationMinutes < 60
                                        ? `${activity.durationMinutes}m`
                                        : `${Math.floor(activity.durationMinutes / 60)}h ${activity.durationMinutes % 60}m`}
                                      size="small"
                                      variant="outlined"
                                    />
                                  )}
                                  {activity.distanceMeters > 0 && (
                                    <Chip
                                      label={activity.distanceMeters >= 1000
                                        ? `${(activity.distanceMeters / 1000).toFixed(2)} km`
                                        : `${activity.distanceMeters} m`}
                                      size="small"
                                      variant="outlined"
                                    />
                                  )}
                                  {activity.activeCalories > 0 && (
                                    <Chip
                                      label={`${activity.activeCalories} kcal`}
                                      size="small"
                                      variant="outlined"
                                      color="warning"
                                    />
                                  )}
                                </Stack>
                                {activity.sourceApp && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                                    via {activity.sourceApp}
                                  </Typography>
                                )}
                              </Box>
                            ))}
                          </Stack>
                        </Box>
                      )}

                      <ActionButton
                        variant="contained"
                        fullWidth
                        onClick={openBodyMeasurementDialog}
                        startIcon={<AddIcon />}
                      >
                        Edit Measurements
                      </ActionButton>

                      {/* Show photo thumbnails if available */}
                      {dailyReport.hasPhoto &&
                        dailyReport.photos &&
                        dailyReport.photos.length > 0 && (
                          <PhotoThumbnails
                            photoURLs={dailyReport.photos.map(
                              (photo) => photo.url,
                            )}
                          />
                        )}
                      {/* Add a keep-alive button for debugging */}
                    </Stack>
                  ) : (
                    <Paper
                      sx={{
                        p: 3,
                        textAlign: "center",
                        bgcolor: "grey.50",
                        borderStyle: "dashed",
                        borderWidth: 2,
                        borderColor: "grey.300",
                      }}
                    >
                      <CalendarIcon
                        sx={{ fontSize: 48, color: "grey.400", mb: 2 }}
                      />
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        No data for this day
                      </Typography>
                      <ActionButton
                        variant="outlined"
                        onClick={openBodyMeasurementDialog}
                      >
                        Add Data
                      </ActionButton>
                    </Paper>
                  )}
                </Box>
              ) : (
                <Paper
                  sx={{
                    p: 3,
                    textAlign: "center",
                    bgcolor: "grey.50",
                    borderStyle: "dashed",
                    borderWidth: 2,
                    borderColor: "grey.300",
                  }}
                >
                  <CalendarIcon
                    sx={{ fontSize: 48, color: "grey.400", mb: 2 }}
                  />
                  <Typography variant="body1" color="text.secondary">
                    Select a day to view details
                  </Typography>
                </Paper>
              )}
            </DailyContent>
          </StyledDailyCard>
        </Grid>
      </Grid>

      {/* Render the dialog if open and selectedDay is set */}
      {bodyMeasurementOpen && selectedDay && (
        <BodyMeasurementsEditDialog
          client={client}
          report={{
            nutrition: {
              reportData: dailyReport?.health?.reportData || {},
              dateIndex: selectedDay,
            },
          }}
          onDialogClosed={handleBodyMeasurementDialogClosed}
        />
      )}
    </Box>
  );
};

export default EnhancedDailyReportsTab;
